/**
 * EncounterSlice Scheduled Procedure Codes Reducers
 * 
 * Reducers for managing encounter scheduled procedure codes with priority ordering.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { ScheduledProcedureCodeType } from '../../types/encounterTypes';
import { EncounterSliceState } from '../state';
import { addEditedField } from '../../../shared/sliceUtils';

// =============================================================================
// SCHEDULED PROCEDURE CODES CRUD REDUCERS
// =============================================================================

/**
 * Add a new scheduled procedure code
 */
export const addScheduledProcedureCode: CaseReducer<
  EncounterSliceState,
  PayloadAction<ScheduledProcedureCodeType>
> = (state, action) => {
  if (state.currentEncounter) {
    if (!state.currentEncounter.scheduledProcedureCodes)
      state.currentEncounter.scheduledProcedureCodes = [];
    state.currentEncounter.scheduledProcedureCodes.push(action.payload);
    addEditedField(state, 'scheduledProcedureCodes');
  }
};

/**
 * Update an existing scheduled procedure code
 */
export const updateScheduledProcedureCode: CaseReducer<
  EncounterSliceState,
  PayloadAction<{ id: string; updates: Partial<ScheduledProcedureCodeType> }>
> = (state, action) => {
  if (state.currentEncounter?.scheduledProcedureCodes) {
    const index = state.currentEncounter.scheduledProcedureCodes.findIndex(
      (code) => code.id === action.payload.id
    );
    if (index !== -1) {
      state.currentEncounter.scheduledProcedureCodes[index] = {
        ...state.currentEncounter.scheduledProcedureCodes[index],
        ...action.payload.updates,
      };
      addEditedField(state, 'scheduledProcedureCodes');
    }
  }
};

/**
 * Remove a scheduled procedure code
 */
export const removeScheduledProcedureCode: CaseReducer<EncounterSliceState, PayloadAction<string>> =
  (state, action) => {
    if (state.currentEncounter?.scheduledProcedureCodes) {
      state.currentEncounter.scheduledProcedureCodes =
        state.currentEncounter.scheduledProcedureCodes.filter((code) => code.id !== action.payload);
      addEditedField(state, 'scheduledProcedureCodes');
    }
  };

/**
 * Set the entire scheduled procedure codes array (replace all)
 */
export const setScheduledProcedureCodes: CaseReducer<
  EncounterSliceState,
  PayloadAction<ScheduledProcedureCodeType[]>
> = (state, action) => {
  if (state.currentEncounter) {
    state.currentEncounter.scheduledProcedureCodes = action.payload;
    addEditedField(state, 'scheduledProcedureCodes');
  }
};

// =============================================================================
// SCHEDULED PROCEDURE CODES PRIORITY REDUCERS
// =============================================================================

/**
 * Move a scheduled procedure code up in priority
 */
export const moveScheduledProcedureCodeUp: CaseReducer<
  EncounterSliceState,
  PayloadAction<string>
> = (state, action) => {
  if (state.currentEncounter?.scheduledProcedureCodes) {
    const codes = state.currentEncounter.scheduledProcedureCodes;
    // Sort by priority first to ensure correct order
    const sortedCodes = [...codes].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const codeIndex = sortedCodes.findIndex((c) => c.id === action.payload);

    if (codeIndex > 0) {
      const currentCode = sortedCodes[codeIndex];
      const previousCode = sortedCodes[codeIndex - 1];

      // Swap priorities
      const tempPriority = currentCode.priority;
      currentCode.priority = previousCode.priority;
      previousCode.priority = tempPriority;

      // Update the original array with the modified codes
      const currentOriginalIndex = codes.findIndex((c) => c.id === currentCode.id);
      const previousOriginalIndex = codes.findIndex((c) => c.id === previousCode.id);

      if (currentOriginalIndex !== -1) {
        codes[currentOriginalIndex] = currentCode;
      }
      if (previousOriginalIndex !== -1) {
        codes[previousOriginalIndex] = previousCode;
      }

      addEditedField(state, 'scheduledProcedureCodes');
    }
  }
};

/**
 * Move a scheduled procedure code down in priority
 */
export const moveScheduledProcedureCodeDown: CaseReducer<
  EncounterSliceState,
  PayloadAction<string>
> = (state, action) => {
  if (state.currentEncounter?.scheduledProcedureCodes) {
    const codes = state.currentEncounter.scheduledProcedureCodes;
    // Sort by priority first to ensure correct order
    const sortedCodes = [...codes].sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const codeIndex = sortedCodes.findIndex((c) => c.id === action.payload);

    if (codeIndex < sortedCodes.length - 1 && codeIndex >= 0) {
      const currentCode = sortedCodes[codeIndex];
      const nextCode = sortedCodes[codeIndex + 1];

      // Swap priorities
      const tempPriority = currentCode.priority;
      currentCode.priority = nextCode.priority;
      nextCode.priority = tempPriority;

      // Update the original array with the modified codes
      const currentOriginalIndex = codes.findIndex((c) => c.id === currentCode.id);
      const nextOriginalIndex = codes.findIndex((c) => c.id === nextCode.id);

      if (currentOriginalIndex !== -1) {
        codes[currentOriginalIndex] = currentCode;
      }
      if (nextOriginalIndex !== -1) {
        codes[nextOriginalIndex] = nextCode;
      }

      addEditedField(state, 'scheduledProcedureCodes');
    }
  }
};
